// src/pages/finance-portal/recurring-bills/RecurringBills.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton, Tooltip, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox, Autocomplete, InputAdornment,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    PAUSED: { bg: '#fef3c7', color: '#92400e' },
    COMPLETED: { bg: '#e0e7ff', color: '#3730a3' },
    CANCELLED: { bg: '#fecaca', color: '#991b1b' },
};

const FREQUENCY_OPTIONS = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'BIWEEKLY', label: 'Bi-weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'YEARLY', label: 'Yearly' },
];

const emptyForm = {
    vendor_id: null,
    frequency: 'MONTHLY',
    amount: '',
    description: '',
    auto_approve: false,
    start_date: '',
    end_date: '',
    lines: [],
};

const emptyLine = { account_id: null, amount: '', description: '' };

export default function RecurringBills() {
    const [bills, setBills] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [processing, setProcessing] = useState(false);

    // Dialog
    const [dialog, setDialog] = useState({ open: false, editing: null });
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Delete
    const [deleteDialog, setDeleteDialog] = useState({ open: false, bill: null });
    const [deleting, setDeleting] = useState(false);

    const statusTabs = ['All', 'Active', 'Paused', 'Completed', 'Cancelled'];
    const statusFilter = tabValue === 0 ? '' : statusTabs[tabValue].toUpperCase();

    const fetchBills = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/recurring-bills', { params });
            if (res.data.success) {
                setBills(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load recurring bills');
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchBills(); }, [fetchBills]);

    useEffect(() => {
        (async () => {
            try {
                const [vRes, aRes] = await Promise.all([
                    axiosInstance.get('/api/finance/vendors', { params: { per_page: 500 } }),
                    axiosInstance.get('/api/finance/coa', { params: { flat: true } }),
                ]);
                if (vRes.data.success) setVendors(vRes.data.data || []);
                if (aRes.data.success) setAccounts(aRes.data.data || []);
            } catch (err) { /* silent */ }
        })();
    }, []);

    const getAccountLabel = (acct) => acct ? `${acct.account_number} - ${acct.name}` : '';
    const findAccount = (id) => accounts.find(a => a.id === id) || null;
    const findVendor = (id) => vendors.find(v => v.id === id) || null;

    const openDialog = (bill = null) => {
        if (bill) {
            setForm({
                vendor_id: bill.vendor_id,
                frequency: bill.frequency || 'MONTHLY',
                amount: bill.amount || '',
                description: bill.description || '',
                auto_approve: bill.auto_approve || false,
                start_date: bill.start_date || '',
                end_date: bill.end_date || '',
                lines: bill.lines && bill.lines.length > 0
                    ? bill.lines.map(l => ({ account_id: l.account_id, amount: l.amount || '', description: l.description || '' }))
                    : [{ ...emptyLine }],
            });
        } else {
            setForm({ ...emptyForm, lines: [{ ...emptyLine }] });
        }
        setDialog({ open: true, editing: bill });
    };

    const updateLine = (idx, field, value) => {
        setForm(prev => {
            const lines = [...prev.lines];
            lines[idx] = { ...lines[idx], [field]: value };
            return { ...prev, lines };
        });
    };

    const saveBill = async () => {
        setSaving(true);
        const payload = {
            vendor_id: form.vendor_id,
            frequency: form.frequency,
            amount: parseFloat(form.amount) || 0,
            description: form.description,
            auto_approve: form.auto_approve,
            start_date: form.start_date,
            end_date: form.end_date || null,
            lines: form.lines.map(l => ({
                account_id: l.account_id,
                amount: parseFloat(l.amount) || 0,
                description: l.description,
            })),
        };
        try {
            if (dialog.editing) {
                await axiosInstance.put(`/api/finance/recurring-bills/${dialog.editing.id}`, payload);
                setSuccess('Recurring bill updated');
            } else {
                await axiosInstance.post('/api/finance/recurring-bills', payload);
                setSuccess('Recurring bill created');
            }
            setDialog({ open: false, editing: null });
            fetchBills();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save recurring bill');
        } finally {
            setSaving(false);
        }
    };

    const handlePause = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/recurring-bills/${id}/pause`, {});
            setSuccess('Recurring bill paused');
            fetchBills();
        } catch (err) { setError(err.response?.data?.message || 'Failed to pause'); }
    };

    const handleResume = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/recurring-bills/${id}/resume`, {});
            setSuccess('Recurring bill resumed');
            fetchBills();
        } catch (err) { setError(err.response?.data?.message || 'Failed to resume'); }
    };

    const handleDelete = async (id) => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/recurring-bills/${id}`);
            setSuccess('Recurring bill deleted');
            setDeleteDialog({ open: false, bill: null });
            fetchBills();
        } catch (err) { setError(err.response?.data?.message || 'Failed to delete'); }
        finally { setDeleting(false); }
    };

    const handleProcessDue = async () => {
        setProcessing(true);
        try {
            const res = await axiosInstance.post('/api/finance/recurring-bills/process', {});
            if (res.data.success) {
                setSuccess(`Processed ${res.data.data?.bills_created || 0} recurring bill(s)`);
            }
            fetchBills();
        } catch (err) { setError(err.response?.data?.message || 'Failed to process'); }
        finally { setProcessing(false); }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RepeatIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Recurring Bills</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Automated recurring vendor bills</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SyncIcon />}
                            onClick={handleProcessDue} disabled={processing}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Process Due
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                        >
                            Create
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    {statusTabs.map(t => <Tab key={t} label={t} />)}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Vendor', 'Frequency', 'Amount', 'Next Run', 'Auto-Approve', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : bills.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No recurring bills found</Typography>
                                    </TableCell></TableRow>
                                ) : bills.map(b => {
                                    const statusStyle = STATUS_COLORS[b.status] || {};
                                    return (
                                        <TableRow key={b.id} hover>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{b.vendor_name || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textTransform: 'capitalize' }}>{b.frequency?.toLowerCase()}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(b.amount)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(b.next_run_date)}</TableCell>
                                            <TableCell>
                                                {b.auto_approve ? (
                                                    <Chip label="Yes" size="small" sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: '#dcfce7', color: '#166534' }} />
                                                ) : (
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>No</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={b.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {b.status === 'ACTIVE' && (
                                                        <Tooltip title="Pause"><IconButton size="small" onClick={() => handlePause(b.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}><PauseIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                    {b.status === 'PAUSED' && (
                                                        <Tooltip title="Resume"><IconButton size="small" onClick={() => handleResume(b.id)} sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}><PlayArrowIcon fontSize="small" /></IconButton></Tooltip>
                                                    )}
                                                    {(b.status === 'ACTIVE' || b.status === 'PAUSED') && (
                                                        <>
                                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(b)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, bill: b })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {dialog.editing ? 'Edit Recurring Bill' : 'Create Recurring Bill'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <Autocomplete
                        options={vendors}
                        value={findVendor(form.vendor_id)}
                        onChange={(_, val) => setForm(prev => ({ ...prev, vendor_id: val?.id || null }))}
                        getOptionLabel={(v) => v.name || ''}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        size="small"
                        renderInput={(params) => (
                            <TextField {...params} label="Vendor"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{option.name}</Typography>
                            </li>
                        )}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
                            <Select value={form.frequency} onChange={e => setForm(prev => ({ ...prev, frequency: e.target.value }))}
                                label="Frequency" sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {FREQUENCY_OPTIONS.map(f => <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Amount" value={form.amount}
                            onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))}
                            size="small" type="number" sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                sx: { fontFamily, borderRadius: '10px' },
                                inputProps: { min: 0, step: '0.01' },
                            }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Box>

                    <TextField label="Description" value={form.description}
                        onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth size="small" multiline rows={2}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Start Date" type="date" value={form.start_date}
                            onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                        <TextField label="End Date (optional)" type="date" value={form.end_date}
                            onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                    </Box>

                    <FormControlLabel
                        control={<Checkbox checked={form.auto_approve} onChange={e => setForm(prev => ({ ...prev, auto_approve: e.target.checked }))}
                            sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Auto-approve generated bills</Typography>}
                    />

                    {/* Lines */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Bill Lines</Typography>
                            <Button size="small" startIcon={<AddIcon />}
                                onClick={() => setForm(prev => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }))}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: ACCENT, fontSize: '0.8rem' }}
                            >
                                Add Line
                            </Button>
                        </Box>
                        <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'visible' }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Account', 'Amount', 'Description', ''].map(h => (
                                                <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {form.lines.map((line, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{ minWidth: 220, p: 1 }}>
                                                    <Autocomplete
                                                        options={accounts}
                                                        value={findAccount(line.account_id)}
                                                        onChange={(_, val) => updateLine(idx, 'account_id', val?.id || null)}
                                                        getOptionLabel={getAccountLabel}
                                                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                                        size="small"
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Account"
                                                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1, width: 140 }}>
                                                    <TextField value={line.amount} onChange={e => updateLine(idx, 'amount', e.target.value)}
                                                        size="small" type="number" placeholder="0.00"
                                                        InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' }, inputProps: { min: 0, step: '0.01' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1 }}>
                                                    <TextField value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                                                        size="small" placeholder="Description"
                                                        InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 1, width: 40 }}>
                                                    {form.lines.length > 1 && (
                                                        <IconButton size="small" onClick={() => setForm(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }))}
                                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                        >
                                                            <RemoveCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveBill}
                        disabled={saving || !form.vendor_id || !form.amount || !form.start_date}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, bill: null })} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Delete Recurring Bill</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                        Are you sure you want to delete this recurring bill? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, bill: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleDelete(deleteDialog.bill?.id)} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
